import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateId } from '../utils/helpers';
import { useCRM } from './CRMContext';
import { useLinkedIn } from './LinkedInContext';

const OutreachContext = createContext();

export const useOutreach = () => useContext(OutreachContext);

export const OutreachProvider = ({ children }) => {
  const { addDeal, addContact, addCompany, addActivity, currentUser } = useCRM();
  const v1 = useLinkedIn();

  // --- STATE V2 ---
  const [campaigns, setCampaigns] = useState([]);
  const [microsites, setMicrosites] = useState([]);
  const [abVariants, setAbVariants] = useState([]);
  const [trackedLinks, setTrackedLinks] = useState([]);
  const [clickEvents, setClickEvents] = useState([]);
  const [leadCaptures, setLeadCaptures] = useState([]);

  // --- MOCK DATA LOAD ---
  useEffect(() => {
    const demoCampaignId = 'camp-1';
    const complianceCampaignId = 'camp-compliance'; 

    setCampaigns([
      { id: demoCampaignId, name: 'HMS Varsling – Demo', status: 'Active', project_id: 'proj-1', owner_user_id: currentUser?.user_id },
      { id: complianceCampaignId, name: 'Compliance Awareness', status: 'Active', project_id: 'proj_standard', owner_user_id: currentUser?.user_id }
    ]);

    setMicrosites([
      {
        id: 'ms-1',
        campaign_id: demoCampaignId,
        slug: 'hmsvarsling-demo',
        title: 'HMS Løsning',
        status: 'Published',
        content: {
          sections: [
            { type: 'Hero', title: 'Forenkle HMS-arbeidet i dag', subtitle: 'Automatisert varsling for moderne bedrifter.', ctaText: 'Få Demo', ctaAction: 'BookMeeting', ctaTarget: 'https://calendly.com/demo' },
            { type: 'Problem', title: 'Bruker dere for mye tid på manuelle lister?', text: 'Gamle systemer stjeler tid fra verdiskapning. Vår løsning frigjør 20% av arbeidsdagen.' },
            { type: 'LeadForm', title: 'Interessert?', fields: ['name', 'email', 'company'] }
          ]
        }
      },
      {
        id: 'ms-compliance',
        campaign_id: complianceCampaignId,
        slug: 'compliance-oversikt',
        title: 'Compliance Oversikt',
        status: 'Published',
        project_scope: "Global",
        tracking_mode: "LinkClickOnly",
        content: {
          sections: [
            { type: 'Hero', title: 'Compliance – oversikt og kontroll', subtitle: 'En kort forklaring på hva compliance betyr i praksis – og hvordan virksomheter jobber strukturert med det i dag.' },
            { type: 'Content', title: 'Hva handler compliance egentlig om?', text: 'For de fleste virksomheter handler compliance ikke om flere rutiner, men om å ha kontroll: tydelige prosesser, dokumentasjon som tåler innsyn, og forutsigbar håndtering av ansvar.' },
            { type: 'List', title: 'Typiske områder virksomheter ønsker oversikt over', items: ['Roller og ansvar', 'Rutiner og etterlevelse', 'Dokumentasjon og sporbarhet', 'Varsling og håndtering av avvik', 'Forberedelse til revisjon eller tilsyn'] },
            { type: 'Content', title: 'En enkel tilnærming', text: 'De fleste starter med en kort gjennomgang for å avklare: Hva er på plass, hva mangler, og hva er faktisk relevant for oss akkurat nå.' },
            { type: 'CTA', title: 'Vil du ha en rask avklaring?', text: 'En uforpliktende prat på 10–15 minutter er ofte nok til å se om dette er relevant.', ctaLabel: 'Avklar på 15 minutter', ctaAction: 'reply_email' }
          ]
        }
      },
      {
        id: 'ms-hms-varsling',
        campaign_id: complianceCampaignId,
        slug: 'hms-varsling-oversikt',
        title: 'HMS Varsling – oversikt',
        status: 'Published',
        project_scope: "Global",
        tracking_mode: "LinkClickOnly",
        content: {
          sections: [
            { type: 'Hero', title: 'HMS Varsling – trygghet i praksis', subtitle: 'En kort oversikt over hva et varslingssystem skal dekke – og hvorfor det handler om mer enn et skjema.' },
            { type: 'Content', title: 'Hva krever regelverket?', text: 'Arbeidsmiljøloven og EUs varslingsdirektiv stiller krav til trygg varsling, konfidensialitet og forsvarlig håndtering.' },
            { type: 'List', title: 'Dette forventes av et varslingssystem', items: ['Trygg og tilgjengelig kanal', 'Beskyttelse av varsler', 'Sporbar og forsvarlig saksbehandling', 'Rolleavklaringer og dokumentasjon'] },
            { type: 'Content', title: 'Vanlige fallgruver', text: 'Mange har løsninger som formelt eksisterer, men som ikke gir trygghet i praksis.' },
            { type: 'CTA', title: 'Ønsker du en rask avklaring?', text: '10–15 minutter er ofte nok.', ctaLabel: 'Avklar HMS Varsling', ctaAction: 'reply_email' }
          ]
        }
      },
      // ROI vs Risiko Microsite
      {
        id: 'roi_vs_risiko_v1',
        campaign_id: complianceCampaignId,
        slug: 'roi-vs-risiko',
        title: 'ROI vs Risiko',
        status: 'Published',
        project_scope: "Global",
        tracking_mode: "LinkClickOnly",
        content: {
          sections: [
            { type: 'Hero', title: 'ROI vs Risiko', subtitle: 'Når lønner det seg å gjøre ingenting – og når er risikoen større enn innsatsen?' },
            { type: 'Content', title: 'Den skjulte kostnaden', text: 'Manglende struktur rundt compliance, HMS eller dokumentasjon blir ofte først synlig når noe går galt – i form av tidsbruk, omdømme eller ansvar.' },
            { type: 'List', title: 'Typiske risikoområder', items: ['Ledelsesansvar og styreansvar', 'Tilsyn og revisjon', 'Varslingssaker', 'Intern uro og omdømme'] },
            { type: 'Content', title: 'Hva koster kontroll?', text: 'I mange tilfeller er investeringen i struktur og oversikt lav – sammenlignet med konsekvensene av manglende kontroll.' },
            { type: 'Content', title: 'En rask realitetssjekk', text: 'En kort gjennomgang gir ofte et tydelig svar på om risikoen er håndterbar – eller bør adresseres.' },
            { type: 'CTA', title: 'Vil du gjøre en rask vurdering?', text: 'Uforpliktende samtale, 10–15 minutter.', ctaLabel: 'Vurder ROI vs Risiko', ctaAction: 'reply_email' }
          ]
        }
      }
    ]);

    // Initial Tracked Links
    setTrackedLinks([
      {
        id: 'link-hms-req',
        short_code: 'hms-var-001',
        destination_url: '/#/ms/hms-varsling-oversikt',
        campaign_id: complianceCampaignId,
        microsite_id: 'ms-hms-varsling',
        signal_type: 'RegulatoryNeed',
        priority_hint: 'Høy',
        confidence: 0.8,
        on_click: {
          create_lead_signal: true,
          update_lead_score: { delta: 20, reason: "Klikk på HMS Varsling-oversikt" },
          fire_ai_coach: true
        },
        created_at: new Date().toISOString()
      },
      // NEW: ROI vs Risiko Tracked Link
      {
        id: 'link-roi-risk-001',
        short_code: 'roi-risk-001',
        destination_url: '/#/ms/roi-vs-risiko',
        campaign_id: complianceCampaignId,
        microsite_id: 'roi_vs_risiko_v1',
        signal_type: 'DecisionInterest',
        priority_hint: 'Høy',
        confidence: 0.75,
        on_click: {
          create_lead_signal: true,
          update_lead_score: { delta: 20, reason: "Klikk på ROI vs Risiko" },
          fire_ai_coach: true
        },
        created_at: new Date().toISOString()
      }
    ]);

    setAbVariants([
      { id: 'var-a', campaign_id: demoCampaignId, name: 'Variant A (Formell)', weight: 50, content_template: "Hei {{FirstName}}, ser dere jobber med HMS. Vi har en løsning..." },
      { id: 'var-b', campaign_id: demoCampaignId, name: 'Variant B (Direkte)', weight: 50, content_template: "Hei {{FirstName}}, spar 20% tid på HMS med vår nye app." }
    ]);
  }, [currentUser]);

  // --- ACTIONS ---
  const generateTrackedLink = (destinationUrl, campaignId, micrositeId, prospectId, variantId) => {
    let selectedVariantId = variantId;
    if (!selectedVariantId && campaigns.length > 0) {
      const vars = abVariants.filter(v => v.campaign_id === campaignId);
      if (vars.length > 0) {
        selectedVariantId = vars[Math.floor(Math.random() * vars.length)].id;
      }
    }

    const shortCode = Math.random().toString(36).substr(2, 7);
    const newLink = {
      id: generateId('link'),
      short_code: shortCode,
      destination_url: destinationUrl,
      campaign_id: campaignId,
      microsite_id: micrositeId,
      variant_id: selectedVariantId,
      prospect_id: prospectId,
      created_at: new Date().toISOString()
    };
    
    setTrackedLinks(prev => [newLink, ...prev]);
    return `${window.location.origin}/#/go/${shortCode}`;
  };

  const logClickEvent = (eventType, linkId, micrositeId, userAgent) => {
    const ipHash = 'hash-' + Math.random().toString(36).substr(2, 9);
    const uaHash = 'ua-' + Math.random().toString(36).substr(2, 9);

    const newEvent = {
      id: generateId('event'),
      event_type: eventType,
      link_id: linkId,
      microsite_id: micrositeId,
      ip_hash: ipHash,
      user_agent_hash: uaHash,
      timestamp: new Date().toISOString()
    };
    setClickEvents(prev => [...prev, newEvent]);
  };

  const saveMicrosite = (microsite) => {
    if (microsite.id) {
      setMicrosites(prev => prev.map(m => m.id === microsite.id ? microsite : m));
    } else {
      setMicrosites(prev => [...prev, { ...microsite, id: generateId('ms') }]);
    }
  };

  const handleLeadFormSubmit = (micrositeId, formData) => {
    const captureId = generateId('capture');
    setLeadCaptures(prev => [...prev, {
      id: captureId,
      microsite_id: micrositeId,
      form_data: formData,
      created_at: new Date().toISOString()
    }]);

    const ms = microsites.find(m => m.id === micrositeId);
    const campaign = campaigns.find(c => c.id === ms?.campaign_id);

    const newCompanyId = generateId('comp');
    addCompany({
      name: formData.company || 'Ukjent Firma',
      status: 'Lead',
      gdpr_status: 'Samtykke',
      notes: `Lead via microsite: ${ms?.title}`,
      company_id: newCompanyId
    });

    const newContactId = generateId('cont');
    addContact({
      contact_id: newContactId,
      company_id: newCompanyId,
      name: formData.name,
      email: formData.email,
      role_in_buying: 'Påvirker'
    });

    if (campaign) {
      addDeal({
        title: `Web Lead: ${formData.company}`,
        company_id: newCompanyId,
        contact_id: newContactId,
        project_id: campaign.project_id,
        value: 50000,
        stage_id: 'stage-1',
        source: 'Microsite',
        notes: `Kampanje: ${campaign.name}`
      });
    }

    addActivity({
      type: 'Annet',
      title: 'Lead skjema mottatt',
      description: `Navn: ${formData.name}, Msg: ${formData.message || '-'}`,
      company_id: newCompanyId,
      status: 'Gjennomført'
    });
  };

  const checkGuardrails = () => {
    const dailyCount = v1.stats.todayCount;
    const limit = v1.stats.dailyLimit;
    return {
      warning: dailyCount > limit,
      message: dailyCount > limit ? `Du har oversteget anbefalt grense på ${limit} handlinger i dag.` : null
    };
  };

  return (
    <OutreachContext.Provider value={{
      ...v1,
      campaigns,
      setCampaigns,
      microsites,
      saveMicrosite,
      abVariants,
      setAbVariants,
      trackedLinks,
      generateTrackedLink,
      clickEvents,
      logClickEvent,
      leadCaptures,
      handleLeadFormSubmit,
      checkGuardrails
    }}>
      {children}
    </OutreachContext.Provider>
  );
};